import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../lib/api';
import toast from 'react-hot-toast';

// 用户类型定义
export interface User {
  id: number;
  username: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  role: string;
  is_premium: boolean;
  premium_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

// 认证上下文类型
interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isPremium: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, display_name?: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refreshUser: () => Promise<void>;
}

// 创建上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 提供者组件属性
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // 初始化：从localStorage恢复登录状态
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          
          // 验证token是否有效
          await authAPI.getCurrentUser();
        } catch (error) {
          console.error('Token验证失败，清除登录状态:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // 登录函数
  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login({ email, password });
      
      const { token, user } = response;
      
      // 保存到状态和localStorage
      setToken(token);
      setUser(user);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('登录成功！');
    } catch (error: any) {
      console.error('登录失败:', error);
      throw new Error(error?.message || '登录失败，请检查邮箱和密码');
    } finally {
      setIsLoading(false);
    }
  };

  // 注册函数
  const register = async (username: string, email: string, password: string, display_name?: string) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register({ username, email, password, display_name });
      
      const { token, user } = response;
      
      // 保存到状态和localStorage
      setToken(token);
      setUser(user);
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('注册成功！');
    } catch (error: any) {
      console.error('注册失败:', error);
      throw new Error(error?.message || '注册失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 登出函数
  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    toast.success('已成功登出');
  };

  // 更新用户信息
  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // 刷新用户信息
  const refreshUser = async () => {
    if (!token) return;
    
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      // 如果token失效，自动登出
      if (error?.status === 401) {
        logout();
      }
    }
  };

  // 计算衍生状态
  const isAuthenticated = !!user && !!token;
  const isPremium = user?.is_premium || false;

  // 上下文值
  const contextValue: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    isPremium,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义hook，方便使用上下文
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth必须在AuthProvider内部使用');
  }
  return context;
};

export default AuthContext;